import React, { useState, useEffect, useContext } from 'react'
import { Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { ReactComponent as AgencyBriefcase } from '../../../assets/svgs/agency-briefcase.svg'
import { ReactComponent as CompanyBuilding } from '../../../assets/svgs/company-building.svg'
import {
  BlurredBackground,
  CompanyDetails,
  CompanyInfoContainer,
  ContentContainer,
  StyledExclamationCircle,
  StyledModalDialog,
  StyledRightArrow,
} from '../../../components/modal/styled'
import apiCompanyUsers from "../../configuration/services/companyUser"
import { userContext } from '../../../contexts/userContext'
import { endSession } from '../../../services/auth/session'

const useCompanyTransitionData = () => {
  const { t } = useTranslation()

  const getCompanyFromStorage = (key) => {
    const companyData = localStorage.getItem(key)
    return companyData ? JSON.parse(companyData) : null
  }

  const currentCompany = getCompanyFromStorage('currentCompany')
  const previousCompany = getCompanyFromStorage('previousCompany')

  const content = t('agencyTypes.companyTransitionContent', {
    prevType: previousCompany?.type === 'AG' ? t('agencyTypes.agency') : t('agencyTypes.organization'),
    currType: currentCompany?.type === 'AG' ? t('agencyTypes.agency') : t('agencyTypes.organization'),
  })

  const currentIcon =
    currentCompany?.type === 'AG' ? (
      <AgencyBriefcase width={60} height={60} />
    ) : (
      <CompanyBuilding width={60} height={60} />
    )
  const previousIcon =
    previousCompany?.type === 'ORG' ? (
      <CompanyBuilding width={60} height={60} />
    ) : (
      <AgencyBriefcase width={60} height={60} />
    )

  return {
    currentCompany,
    previousCompany,
    content,
    currentIcon,
    previousIcon,
  }
}

function CompanyTransitionModal() {
  const { state: user } = useContext(userContext)
  const {
    currentCompany,
    previousCompany,
    content,
    currentIcon,
    previousIcon,
  } = useCompanyTransitionData()

  const [modalDisplayType, setModalDisplayType] = useState('block')
  const [isInitialMount, setIsInitialMount] = useState(true)

  const handleLogout = () => {
    localStorage.clear()
    endSession().finally(() => { window.location.href = '/login' })
  }

  useEffect(() => {
    if (isInitialMount) {
      setIsInitialMount(false)
    }
  }, [])

  useEffect(() => {
    if (user?.companyId) {
      const storedCurrent = localStorage.getItem('currentCompany')
      const current = storedCurrent ? JSON.parse(storedCurrent) : null

      if (!current || current.id !== user.companyId) {
        if (current) {
          localStorage.setItem('previousCompany', JSON.stringify(current))
        }
        const newCompany = {
          id: user.companyId,
          type: user.companyType,
          name: user.companyName,
        }
        localStorage.setItem('currentCompany', JSON.stringify(newCompany))
      }
    }
  }, [user?.companyId])

  useEffect(() => {
    if (
      !currentCompany ||
      !previousCompany ||
      currentCompany.id === previousCompany.id ||
      isInitialMount ||
      !sessionStorage.getItem('companyChanged')
    ) {
      return undefined;
    }

    let isMounted = true;

    const checkAdminAndLogout = async () => {
      const isNotAdmin = await apiCompanyUsers.checkNotAdminUser();
      if (isMounted && !isNotAdmin) handleLogout();
    };

    checkAdminAndLogout();

    const timer = setTimeout(() => {
      if (isMounted) {
        setModalDisplayType('none');
        sessionStorage.removeItem('companyChanged');
      }
    }, 2000);

    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [currentCompany, previousCompany, isInitialMount]);

  if (
    !currentCompany ||
    !previousCompany ||
    currentCompany.id === previousCompany.id ||
    isInitialMount ||
    !sessionStorage.getItem('companyChanged')
  ) {
    return null
  }

  return (
    <>
      <BlurredBackground modalDisplayType={modalDisplayType} />
      <StyledModalDialog modalDisplayType={modalDisplayType}>
        <Modal.Body>
          <div className="d-flex justify-content-center align-items-center">
            <StyledExclamationCircle />
          </div>
          <ContentContainer>
            <p>{content}</p>
          </ContentContainer>
          <CompanyInfoContainer>
            <CompanyDetails>
              {previousIcon}
              <p>
                {previousCompany?.type} {previousCompany?.type && '-'}{' '}
                {previousCompany?.name}
              </p>
            </CompanyDetails>
            <StyledRightArrow />
            <CompanyDetails>
              {currentIcon}
              <p>
                {currentCompany?.type} {currentCompany?.type && '-'}{' '}
                {currentCompany?.name}
              </p>
            </CompanyDetails>
          </CompanyInfoContainer>
        </Modal.Body>
      </StyledModalDialog>
    </>
  )
}

export default React.memo(CompanyTransitionModal)